import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { CheckCircle2, ShieldCheck, ArrowLeft } from "lucide-react";

const MIN_LEN = 12;

function strengthChecks(pw) {
  return {
    length: pw.length >= MIN_LEN,
    letter: /[A-Za-z]/.test(pw),
    digit: /\d/.test(pw),
    // eslint-disable-next-line no-useless-escape
    symbol: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?`~]/.test(pw),
  };
}

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const checks = useMemo(() => strengthChecks(pw), [pw]);
  const allValid = checks.length && checks.letter && checks.digit && checks.symbol;
  const passwordsMatch = pw === confirm && pw.length > 0;
  const canSubmit = !!token && allValid && passwordsMatch && !loading;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!token) { setErr("Missing reset token. Please use the link from your email."); return; }
    if (!allValid) { setErr("Password doesn't meet the strength requirements."); return; }
    if (!passwordsMatch) { setErr("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-md mx-auto my-16" data-testid="reset-password-success">
        <div className="card-surface p-8 text-center space-y-4">
          <div className="w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-7 h-7 text-emerald-400" strokeWidth={1.5} />
          </div>
          <h1 className="font-serif text-3xl text-[#FDFBF7]">Password reset</h1>
          <p className="text-[#A1A4AB]">You can now sign in with your new password.</p>
          <Link to="/login" className="btn-primary inline-flex" data-testid="reset-to-login-link">Sign in</Link>
        </div>
      </div>
    );
  }

  const Rule = ({ ok, label }) => (
    <div className={`flex items-center gap-2 text-xs ${ok ? "text-emerald-400" : "text-[#6C707A]"}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${ok ? "bg-emerald-400" : "bg-[#22262E]"}`} />
      {label}
    </div>
  );

  if (!token) {
    return (
      <div className="max-w-md mx-auto my-16" data-testid="reset-missing-token">
        <div className="card-surface p-8 text-center space-y-4">
          <h1 className="font-serif text-3xl text-[#FDFBF7]">Invalid reset link</h1>
          <p className="text-[#A1A4AB]">
            This reset link is invalid or missing. Please request a new one.
          </p>
          <Link to="/forgot-password" className="btn-primary inline-flex">Request new link</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto my-16">
      <div className="overline text-[#C5A059] mb-3 text-center">New password</div>
      <h1 className="font-serif text-4xl text-center text-[#FDFBF7] tracking-tight mb-2">Set your password</h1>
      <p className="text-center text-[#A1A4AB] mb-8">Choose a strong password you haven't used before.</p>
      <form onSubmit={submit} className="space-y-4 card-surface p-8" data-testid="reset-password-form">
        <div>
          <label className="text-sm text-[#A1A4AB] mb-2 block">New password</label>
          <input type="password" required value={pw} onChange={(e) => setPw(e.target.value)}
                 className="input-lux" data-testid="reset-new-password" placeholder="••••••••••••" />
          <div className="mt-3 grid grid-cols-2 gap-1.5">
            <Rule ok={checks.length} label={`At least ${MIN_LEN} characters`} />
            <Rule ok={checks.letter} label="A letter" />
            <Rule ok={checks.digit} label="A digit" />
            <Rule ok={checks.symbol} label="A symbol" />
          </div>
        </div>
        <div>
          <label className="text-sm text-[#A1A4AB] mb-2 block">Confirm password</label>
          <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
                 className="input-lux" data-testid="reset-confirm-password" placeholder="••••••••••••" />
          {confirm.length > 0 && !passwordsMatch && (
            <div className="text-xs text-red-400 mt-1" data-testid="reset-mismatch">Passwords don't match.</div>
          )}
        </div>
        {err && <div className="text-sm text-red-400" data-testid="reset-error">{err}</div>}
        <button type="submit" disabled={!canSubmit} className="btn-primary w-full justify-center" data-testid="reset-submit-btn">
          <ShieldCheck className="w-4 h-4" strokeWidth={1.5} /> {loading ? "Resetting..." : "Reset password"}
        </button>
        <div className="text-center text-sm text-[#A1A4AB] pt-2">
          <Link to="/login" className="text-[#C5A059] hover:text-[#DCC286] inline-flex items-center gap-1">
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} /> Back to sign in
          </Link>
        </div>
      </form>
    </div>
  );
}
